import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, LogOut, ChevronDown } from "lucide-react";
import { useAuthStore } from "@/store/useAuthStore";
import { useTheme } from "@/context/ThemeContext";
import { useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { PERMISSIONS } from "@/constants/permissions";
import { getBFFProductTaxonomyNavItems } from "@/constants/bffProductSegment";
import { resolveAvatarUrl } from "@/utils/avatarUrl";
import { NavIcon } from "@/components/Sidebar/NavIcon";
import { hasPermission as checkPermission } from "@/lib/utils";
import { useNavigate } from "react-router";

/**
 * Sidebar Component
 * Full-height sidebar that slides from right to left
 * Has curved bottom edge that aligns with the active navbar button
 */
export function Sidebar({
  isOpen,
  onClose,
  windowWidth,
  centerPos,
  curveWidth,
  curveDepth,
}) {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const { permissions } = useUserPermissions();
  const sidebarAvatarUrl = resolveAvatarUrl(user?.avatar);

  const hasPermission = (perm) => checkPermission(permissions, perm);

  const NAV_ITEMS = [
    // 1. Clients (direct link, matching desktop sidebar)
    ...(hasPermission(PERMISSIONS.CLIENT.READ) ? [{
      id: "clients",
      label: "Clients",
      icon: (props) => <NavIcon name="team-management" {...props} />,
      iconDark: (props) => <NavIcon name="team-management" {...props} />,
      path: "/clients",
      subItems: [],
    }] : []),

    // 2. Campaigns (matching desktop sidebar)
    ...(hasPermission(PERMISSIONS.CAMPAIGN.READ) ? [{
      id: "campaigns",
      label: "Campaigns",
      icon: (props) => <NavIcon name="campaigns" {...props} />,
      iconDark: (props) => <NavIcon name="campaigns" {...props} />,
      subItems: [
        { id: "applied-sample-promotion", label: "Applied Sample Promotion", path: "/campaigns/applied-sample-promotion" },
        { id: "bff-product-promotion", label: "BFF Product Promotion", path: "/campaigns/bff-product-promotion" },
        { id: "promotion-adoption-report", label: "Promotion & Adoption Report", path: "/campaigns/promotion-adoption-report" },
      ],
    }] : []),

    // 3. BFF Product List (matching desktop sidebar)
    ...(() => {
      const bffPerm = PERMISSIONS.BFF_PRODUCT || PERMISSIONS.BFF_PRODUCT_CODE;
      const bffTaxPerm = PERMISSIONS.BFF_PRODUCT_SEGMENT || PERMISSIONS.BFF_PRODUCT_TAXONOMY;
      const subItems = [
        ...(hasPermission(bffPerm.READ)
          ? [{ id: "bff-product-list", label: "Product List", path: "/bff-product/list" }]
          : []),
        ...(hasPermission(bffTaxPerm.READ) || hasPermission("bff-product-taxonomy:read")
          ? getBFFProductTaxonomyNavItems()
          : []),
      ];
      return subItems.length > 0
        ? [{
            id: "bff-product",
            label: "BFF Product List",
            icon: (props) => <NavIcon name="bff-product-list" {...props} />,
            iconDark: (props) => <NavIcon name="bff-product-list" {...props} />,
            subItems,
          }]
        : [];
    })(),

    // 4. CRM Settings (matching desktop sidebar)
    ...(() => {
      const subItems = [
        ...(hasPermission(PERMISSIONS.CRM_SALES_TEAM.READ) ? [{ id: "settings-sales-teams", label: "Sales Teams", path: "/settings/sales-teams" }] : []),
        ...(hasPermission(PERMISSIONS.CRM_DEFAULT_FOLLOWER.READ) ? [{ id: "settings-default-followers", label: "Default Followers", path: "/settings/default-followers" }] : []),
        ...(hasPermission(PERMISSIONS.CRM_DEFAULT_MANAGER.READ) ? [{ id: "settings-default-managers", label: "Default CRM Managers", path: "/settings/default-managers" }] : []),
        ...(hasPermission(PERMISSIONS.CRM_MARKET_CATEGORY.READ) ? [{ id: "settings-market-categories", label: "CRM Market", path: "/settings/market-categories" }] : []),
        ...(hasPermission(PERMISSIONS.CRM_CATEGORY.READ) ? [{ id: "settings-categories", label: "Categories", path: "/settings/categories" }] : []),
        ...(hasPermission(PERMISSIONS.CRM_TAG.READ) ? [{ id: "settings-tags", label: "CRM Tags", path: "/settings/tags" }] : []),
      ];
      return [{
        id: "settings",
        label: "CRM Settings",
        icon: (props) => <NavIcon name="settings" {...props} />,
        iconDark: (props) => <NavIcon name="settings" {...props} />,
        subItems,
      }];
    })(),

    // 5. Invites and Access (matching desktop sidebar)
    ...(() => {
      const subItems = [
        ...(hasPermission(PERMISSIONS.INVITATION.READ) ? [{ id: "emp-invites", label: "Employee Invitations", path: "/employee-invitations" }] : []),
        ...(hasPermission(PERMISSIONS.USER.READ) ? [{ id: "emp-mgmt", label: "Employee Management", path: "/employee-management" }] : []),
        ...(hasPermission(PERMISSIONS.ROLE.READ) ? [{ id: "access-mgmt", label: "Access Management", path: "/access-management" }] : []),
        ...(hasPermission(PERMISSIONS.AUDIT.READ) ? [{ id: "history", label: "History", path: "/access-history" }] : []),
      ];
      return subItems.length > 0 ? [{
        id: "invites",
        label: "Invites and Access",
        icon: (props) => <NavIcon name="invite-access" {...props} />,
        iconDark: (props) => <NavIcon name="invite-access" {...props} />,
        subItems,
      }] : [];
    })(),
  ];
  const cornerRadius = 16;
  const sidebarWidthPercent = 0.95;
  const sidebarWidth = windowWidth * sidebarWidthPercent;
  const navbarHeight = 83;
  // Use a ref to track actual rendered height
  const [sidebarHeight, setSidebarHeight] = React.useState(
    window.innerHeight - navbarHeight,
  );

  React.useEffect(() => {
    const updateHeight = () => {
      setSidebarHeight(window.innerHeight - navbarHeight);
    };
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      // Ignore
    }
    logout();
    queryClient.clear();
    onClose();
  };

  // Calculate where the More button is relative to the sidebar's left edge
  const sidebarLeftPosition = windowWidth - sidebarWidth;
  const curvePositionInSidebar = centerPos - sidebarLeftPosition;

  // Sidebar Path with curve cutout at bottom
  const sidebarPath = `
    M ${cornerRadius} 0
    L ${sidebarWidth} 0
    L ${sidebarWidth} ${sidebarHeight - cornerRadius}
    Q ${sidebarWidth} ${sidebarHeight}, ${
      sidebarWidth - cornerRadius
    } ${sidebarHeight}
    L ${curvePositionInSidebar + curveWidth / 2} ${sidebarHeight}
    C ${curvePositionInSidebar + curveWidth / 4} ${sidebarHeight},
      ${curvePositionInSidebar + curveWidth / 4} ${sidebarHeight - curveDepth},
      ${curvePositionInSidebar} ${sidebarHeight - curveDepth}
    C ${curvePositionInSidebar - curveWidth / 4} ${sidebarHeight - curveDepth},
      ${curvePositionInSidebar - curveWidth / 4} ${sidebarHeight},
      ${curvePositionInSidebar - curveWidth / 2} ${sidebarHeight}
    L ${cornerRadius} ${sidebarHeight}
    Q 0 ${sidebarHeight}, 0 ${sidebarHeight - cornerRadius}
    L 0 ${cornerRadius}
    Q 0 0, ${cornerRadius} 0
    Z
  `;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-40 bg-black/50 md:hidden"
        onClick={onClose}
      />

      {/* Sidebar Container */}
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="fixed right-0 z-50 pointer-events-auto md:hidden"
        style={{
          width: `${sidebarWidthPercent * 100}%`,
          height: `calc(100dvh - ${navbarHeight}px)`,
          bottom: `${navbarHeight}px`,
          top: 0,
        }}
      >
        {/* Sidebar Background SVG */}
        <div className="absolute inset-0 w-full h-full pointer-events-none ">
          <svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${sidebarWidth} ${sidebarHeight}`}
            preserveAspectRatio="none"
            className="w-full h-full"
          >
            <defs>
              <clipPath id="customSidebarClip">
                <path
                  d={`
                    M 16 0
                    L ${sidebarWidth} 0
                    L ${sidebarWidth} ${sidebarHeight - 16}
                    Q ${sidebarWidth} ${sidebarHeight} ${
                      sidebarWidth - 16
                    } ${sidebarHeight}
                    L 16 ${sidebarHeight}
                    Q 0 ${sidebarHeight} 0 ${sidebarHeight - 16}
                    L 0 16
                    Q 0 0 16 0
                    Z
                  `}
                />
              </clipPath>
            </defs>
            <motion.path
              d={sidebarPath}
              className="fill-background "
              clipPath="url(#customSidebarClip)"
              animate={{ d: sidebarPath }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            />
          </svg>
        </div>

        {/* Sidebar Content Container */}
        <div className="relative z-50 flex flex-col h-full pointer-events-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4">
            {/* Empty header or Logo could go here if needed, but design shows list starting high */}
            <div />
            <button
              onClick={onClose}
              className="p-2 transition-colors rounded-full hover:bg-primary-shade-2"
              aria-label="Close menu"
            >
              <X size={24} className="text-foreground" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 px-4 py-2 overflow-y-auto">
            {NAV_ITEMS.map((item) => (
              <SidebarItem
                key={item.id}
                label={item.label}
                icon={item.icon}
                iconDark={item.iconDark}
                href={item.path}
                subItems={item.subItems}
                theme={theme}
                onClose={onClose}
              />
            ))}
          </div>

          {/* User Profile & Logout Section */}
          <div className="px-6 pt-4 pb-12">
            <div
              onClick={() => {
                navigate("/profile");
                onClose?.();
              }}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FFC107] overflow-hidden flex items-center justify-center">
                  {sidebarAvatarUrl ? (
                    <img
                      src={sidebarAvatarUrl}
                      alt="avatar"
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    <span className="text-sm font-bold text-black">
                      {user?.name?.charAt(0)?.toUpperCase() || "U"}
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground">
                    {user?.name || "Abu Zafar"}
                  </p>
                  <p className="text-xs text-foreground/70">
                    {user?.email || "zafar.ikbal@bangaflavour.com"}
                  </p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center justify-center w-10 h-10 transition-colors rounded-lg hover:bg-primary-shade-2"
                aria-label="Logout"
              >
                <LogOut size={20} className="text-nav-highlight" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
}

/**
 * SidebarSection Component
 * Kept for compatibility if imported elsewhere, but not used in this new design
 */
export function SidebarSection({ title, children }) {
  return (
    <div className="px-4 py-3">
      {title && (
        <h3 className="mb-3 text-sm font-semibold uppercase text-foreground/60">
          {title}
        </h3>
      )}
      <div className="space-y-1">{children}</div>
    </div>
  );
}

/**
 * SidebarItem Component
 * Individual item within the sidebar - supports sub-items with expand/collapse
 */
export function SidebarItem({
  label,
  icon: Icon,
  iconDark: IconDark,
  onClick,
  href,
  subItems,
  active = false,
  theme,
  onClose,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasSubItems = subItems && subItems.length > 0;

  const handleClick = () => {
    if (hasSubItems) {
      setIsExpanded(!isExpanded);
    } else if (onClick) {
      onClick();
    } else if (href) {
      window.location.href = href;
      onClose?.();
    }
  };

  const handleSubItemClick = (path) => {
    window.location.href = path;
    onClose?.();
  };

  // Choose icon based on theme
  const DisplayIcon = theme === "dark" && IconDark ? IconDark : Icon;

  return (
    <div>
      <button
        onClick={handleClick}
        className={`flex items-center w-full gap-4 px-3 py-3 text-left transition-colors rounded-lg group hover:bg-gray-100 dark:hover:bg-gray-800`}
      >
        {DisplayIcon && (
          <div className="flex items-center justify-center w-6 h-6">
            <DisplayIcon
              className={`w-6 h-6 ${
                theme === "dark" ? "text-white" : "text-[#2c213d]"
              }`}
            />
          </div>
        )}
        <span
          className={`text-lg font-semibold flex-1 ${
            theme === "dark" ? "text-white" : "text-[#2c213d]"
          }`}
        >
          {label}
        </span>
        {hasSubItems && (
          <ChevronDown
            className={`w-5 h-5 transition-transform duration-200 ${
              isExpanded ? "rotate-180" : ""
            } ${theme === "dark" ? "text-white" : "text-[#2c213d]"}`}
          />
        )}
      </button>

      {/* Sub-items */}
      {hasSubItems && (
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isExpanded ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="pl-12 pr-3 space-y-1">
            {subItems.map((subItem) => (
              <button
                key={subItem.id}
                onClick={() => handleSubItemClick(subItem.path)}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 ${
                  theme === "dark" ? "text-white/80" : "text-[#2c213d]/80"
                }`}
              >
                {subItem.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

